/**
 * The People directory's profile drawer — Task T2-B.
 *
 * Reuses the drawer pattern `attendance/DayDrawer.tsx` established: `role="dialog"`
 * with deliberately **no `aria-modal`** (nothing outside it is inert, and there is
 * no focus trap — DayDrawer's own header explains why one implemented incorrectly
 * is worse than none), focus lands on the drawer's own heading on open, and `Esc`
 * closes it.
 *
 * A separate `GET /employees/{employeeId}` read rather than a lookup into the page
 * already in memory — `DayDrawer`'s own reason applies here too: this is the read
 * that carries the manager brief and `direct_reports_count`, which the list
 * response (`EmployeeDirectoryRow`) does not.
 *
 * ── Quick links: only where the target screen actually reads the id ──────────
 *
 * Checked against every candidate screen's own source before this file linked to
 * it:
 *
 *  - `/attendance` reads `employee_id` from its own URL to seed its subject
 *    (`AttendanceScreen.tsx`'s own `seededEmployee`) — linked, pre-filtered.
 *  - `/geo`'s team panel keeps its employee id in local component state, never the
 *    URL (`GeoScreen.tsx` has no `useSearchParams` at all) — no link.
 *  - `/presence` has no employee filter of any kind, only status/department/office
 *    (`PresenceScreen.tsx`) — no link.
 *  - `/effectiveness` has no per-employee URL parameter either — it reads the
 *    caller's own row or the team distribution, never one named employee
 *    (`EffectivenessScreen.tsx`) — no link.
 *
 * So exactly one quick link renders. `people.drawer.quickLinks.note` says why the
 * other three are absent, on screen, rather than leaving a reader to wonder if the
 * omission was an oversight.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { Link } from 'react-router';

import { useI18n } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import { qs } from '../../api/paths';
import { employeePath, type EmployeeProfile } from '../../api/employees';
import { useApiQuery } from '../../api/useApiQuery';
import { useSession } from '../../auth/SessionProvider';
import { EMPLOYEE_STATUS_PRESENTATION, employeeStatusOf } from '../../people/status';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { Skeleton } from '../../states/Skeleton';

const ROLE_LABEL_KEY = {
  ceo: 'shell.role.ceo',
  manager: 'shell.role.manager',
  employee: 'shell.role.employee',
} as const;

export interface PeopleDrawerProps {
  readonly employeeId: string;
  readonly onClose: () => void;
  /** Drills the drawer into another employee's profile — used by the manager link. */
  readonly onSelectEmployee: (employeeId: string) => void;
}

export function PeopleDrawer({
  employeeId,
  onClose,
  onSelectEmployee,
}: PeopleDrawerProps): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const timezone = user?.timezone ?? 'UTC';
  const { status, data, error, reload } = useApiQuery<EmployeeProfile>(employeePath(employeeId));
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, [employeeId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const title =
    data === null
      ? t('people.drawer.title', { name: employeeId })
      : t('people.drawer.title', { name: data.full_name });

  return (
    <aside
      className="wp-drawer"
      role="dialog"
      aria-labelledby="people-drawer-title"
      data-testid="people-drawer"
    >
      <div className="wp-screen-head">
        <h2 className="wp-section__title" id="people-drawer-title" tabIndex={-1} ref={headingRef}>
          {title}
        </h2>
        <button
          type="button"
          className="wp-button"
          onClick={onClose}
          data-testid="people-drawer-close"
        >
          {t('people.drawer.close')}
        </button>
      </div>

      {status === 'loading' ? (
        <Skeleton
          rows={4}
          variant="block"
          label={t('people.drawer.loading')}
          testId="people-drawer-skeleton"
        />
      ) : null}

      {status === 'error' && error !== null ? (
        <ApiErrorNote
          error={error}
          onRetry={reload}
          subject={t('people.drawer.subject')}
          testId="people-drawer-error"
        />
      ) : null}

      {status === 'ok' && data !== null ? (
        <div className="wp-stack" data-testid="people-drawer-profile">
          <p>
            <Badge
              severity={EMPLOYEE_STATUS_PRESENTATION[employeeStatusOf(data)].severity}
              icon={EMPLOYEE_STATUS_PRESENTATION[employeeStatusOf(data)].icon}
              label={t(EMPLOYEE_STATUS_PRESENTATION[employeeStatusOf(data)].labelKey)}
              testId="people-drawer-status"
            />
          </p>

          <dl className="wp-keys" data-testid="people-drawer-fields">
            <div className="wp-keys__item">
              <dt>{t('people.drawer.field.email')}</dt>
              <dd data-testid="people-drawer-email">{data.email}</dd>
            </div>
            <div className="wp-keys__item">
              <dt>{t('people.drawer.field.role')}</dt>
              <dd data-testid="people-drawer-role">{t(ROLE_LABEL_KEY[data.role])}</dd>
            </div>
            <div className="wp-keys__item">
              <dt>{t('people.drawer.field.jobTitle')}</dt>
              <dd data-testid="people-drawer-jobtitle">{data.job_title ?? '—'}</dd>
            </div>
            <div className="wp-keys__item">
              <dt>{t('people.drawer.field.department')}</dt>
              <dd data-testid="people-drawer-department">
                {data.department_name ?? t('people.noDepartment')}
              </dd>
            </div>
            <div className="wp-keys__item">
              <dt>{t('people.drawer.field.branch')}</dt>
              <dd data-testid="people-drawer-branch">{data.branch_name ?? '—'}</dd>
            </div>
            <div className="wp-keys__item">
              <dt>{t('people.drawer.field.manager')}</dt>
              <dd data-testid="people-drawer-manager">
                {data.manager === null ? (
                  t('people.noManager')
                ) : (
                  <button
                    type="button"
                    className="wp-linklike"
                    data-testid="people-drawer-manager-link"
                    onClick={() => {
                      if (data.manager !== null) onSelectEmployee(data.manager.id);
                    }}
                  >
                    {data.manager.full_name}
                  </button>
                )}
              </dd>
            </div>
            <div className="wp-keys__item">
              <dt>{t('people.drawer.field.directReports')}</dt>
              <dd data-testid="people-drawer-direct-reports">{data.direct_reports_count}</dd>
            </div>
            <div className="wp-keys__item">
              <dt>{t('people.drawer.field.syncedAt')}</dt>
              <dd data-testid="people-drawer-synced-at">
                {formatInstant(data.synced_at, locale, timezone)}
              </dd>
            </div>
            {data.offboarded_at === null ? null : (
              <div className="wp-keys__item">
                <dt>{t('people.drawer.field.offboardedAt')}</dt>
                <dd data-testid="people-drawer-offboarded-at">
                  {formatInstant(data.offboarded_at, locale, timezone)}
                </dd>
              </div>
            )}
          </dl>

          <section
            aria-labelledby="people-drawer-quicklinks-heading"
            data-testid="people-drawer-quicklinks"
          >
            <h3 className="wp-section__title" id="people-drawer-quicklinks-heading">
              {t('people.drawer.quickLinks.heading')}
            </h3>
            <p>
              <Link
                to={`/attendance${qs({ employee_id: data.id })}`}
                className="wp-button"
                data-testid="people-drawer-link-attendance"
              >
                {t('people.drawer.quickLinks.attendance')}
              </Link>
            </p>
            <p className="wp-small wp-muted" data-testid="people-drawer-quicklinks-note">
              {t('people.drawer.quickLinks.note')}
            </p>
          </section>
        </div>
      ) : null}
    </aside>
  );
}
